import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  StatusPill,
  LoadingState,
  ErrorState,
  EmptyState,
  SearchIcon,
} from '../../components';
import { api, useApi } from '../../api';
import type { OrderSummaryView } from '../../api';
import { OrderStatus, PaymentMethod } from '@jalupro/shared';
import { useMoneyVisible } from '../../auth';
import { useOffices } from '../../offices';
import { useT } from '../../i18n';
import dict from './OrdersQueue.i18n';

/**
 * Admin · Orders — the oversight queue. Route /admin/orders.
 * Ported pixel-faithfully from Screen5B_QueueClean (design-source/screen-5-admin-queue.jsx).
 *
 * The layout (PortalLayout/AdminLayout) supplies <CleanSidebar>; this screen renders
 * only the content to the right of it: a saved-views rail + quick filters, a topbar,
 * and the "Needs attention" table with row tinting + Resolve/Open actions.
 *
 * Wired to GET /orders (admin scope). The wireframe's hardcoded saved views are
 * implemented as real, client-driven filters over the live order list. Phase-1 scope:
 * payments are PREPAID-only and ON_HOLD_ADMIN_REVIEW is never auto-triggered, so the
 * source's SEPA / 30d-postpay views + quick-filter chips are dropped (see deviations).
 */

/** A saved view = a label key + a predicate over the live rows + an optional server-side filter. */
interface SavedView {
  readonly key: string;
  /** Translation key for the view name (resolved in the component via `t`). */
  readonly nameKey: string;
  /** Translation key for the one-line description shown under the heading. */
  readonly descKey: string;
  /** Server-side status narrowing (sent to GET /orders) where it cleanly maps. */
  readonly status?: OrderStatus;
  /** Server-side archived toggle: true → list archived (soft-deleted) orders only. */
  readonly archived?: boolean;
  /** Client-side refinement applied on top of the fetched page (attention heuristics). */
  readonly match?: (row: OrderSummaryView) => boolean;
}

const PAGE_SIZE = 50;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Days since a timestamp (whole days, floored). */
function daysSince(iso: string): number {
  return Math.floor((Date.now() - new Date(iso).getTime()) / MS_PER_DAY);
}

/** Compact human age from a timestamp, matching the wireframe's "30m / 5h / 4d" copy. */
function ageLabel(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${Math.max(mins, 0)}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

/** Short date like "15 May" from an ISO timestamp. */
function dateLabel(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

/** Euro total from integer cents, e.g. 231000 → "€2,310". */
function euros(cents: number): string {
  return `€${Math.round(cents / 100).toLocaleString('en-GB')}`;
}

const PAYMENT_LABEL: Record<PaymentMethod, string> = {
  STRIPE: 'Stripe',
  PAYPAL: 'PayPal',
  IBAN: 'IBAN',
};

type AttentionKind = 'stuck' | 'delivery' | null;

/**
 * Phase-1 attention heuristics (no SEPA/postpay holds): a proforma with no payment for
 * more than 3 days is "stuck"; a SHIPPED order that diverged is a "delivery" problem.
 */
function attentionOf(row: OrderSummaryView): AttentionKind {
  if (row.status === OrderStatus.DELIVERY_PROBLEM) return 'delivery';
  if (row.status === OrderStatus.PROFORMA_SENT && daysSince(row.createdAt) > 3) return 'stuck';
  return null;
}

/** Tint a row by its attention kind (matches the source's red/amber wash). */
function rowTint(kind: AttentionKind): string {
  if (kind === 'stuck' || kind === 'delivery') return 'var(--red-bg)';
  return 'transparent';
}

const VIEWS: readonly SavedView[] = [
  { key: 'all', nameKey: 'view_allorders', descKey: 'desc_allorders' },
  { key: 'recent', nameKey: 'view_recent', descKey: 'desc_recent', match: (r) => daysSince(r.createdAt) <= 30 },
  { key: 'attention', nameKey: 'view_attention', descKey: 'desc_attention', match: (r) => attentionOf(r) !== null },
  {
    key: 'iban',
    nameKey: 'view_iban',
    descKey: 'desc_iban',
    status: OrderStatus.PROFORMA_SENT,
    match: (r) => r.paymentMethod === PaymentMethod.IBAN,
  },
  {
    key: 'stuck',
    nameKey: 'view_stuck',
    descKey: 'desc_stuck',
    status: OrderStatus.PROFORMA_SENT,
    match: (r) => attentionOf(r) === 'stuck',
  },
  { key: 'shipped', nameKey: 'view_shipped', descKey: 'desc_shipped', status: OrderStatus.SHIPPED },
  { key: 'delivery', nameKey: 'view_delivery', descKey: 'desc_delivery', status: OrderStatus.DELIVERY_PROBLEM },
  { key: 'open', nameKey: 'view_open', descKey: 'desc_open', match: (r) => r.status !== OrderStatus.DELIVERED },
  { key: 'archived', nameKey: 'view_archived', descKey: 'desc_archived', archived: true },
];

/** Phase-1 payment-method quick-filters (no SEPA / 30d). */
const METHOD_FILTERS: readonly PaymentMethod[] = [
  PaymentMethod.STRIPE,
  PaymentMethod.PAYPAL,
  PaymentMethod.IBAN,
];

export default function AdminOrdersQueue() {
  const navigate = useNavigate();
  const { activeOffices } = useOffices();
  const t = useT(dict);
  const showMoney = useMoneyVisible();
  // Deep-linkable initial view (e.g. the dashboard's "Open queue" → ?view=attention).
  const [searchParams] = useSearchParams();
  const requestedView = searchParams.get('view');
  const [viewKey, setViewKey] = useState<SavedView['key']>(
    VIEWS.some((v) => v.key === requestedView) ? (requestedView as string) : 'all',
  );
  const [search, setSearch] = useState('');
  const [methodFilter, setMethodFilter] = useState<PaymentMethod | null>(null);

  const view = VIEWS.find((v) => v.key === viewKey) ?? VIEWS[0];

  const { data, loading, error, reload } = useApi(
    () =>
      api.orders.list({
        status: view.status,
        ...(view.archived ? { archived: 'true' as const } : {}),
        search: search.trim() || undefined,
        page: 1,
        pageSize: PAGE_SIZE,
      }),
    [view.status, view.archived, search],
  );

  // Client-side refinement: apply the view's attention predicate + the active method chip.
  const rows = useMemo(() => {
    const items = data?.items ?? [];
    return items.filter((r) => {
      if (view.match && !view.match(r)) return false;
      if (methodFilter && r.paymentMethod !== methodFilter) return false;
      return true;
    });
  }, [data, view, methodFilter]);

  // Live per-view counts over the fetched page (the rail badges). The archived view
  // lists a different population (archived-only), so its count is skipped.
  const counts = useMemo(() => {
    const items = data?.items ?? [];
    const map: Record<string, number> = {};
    for (const v of VIEWS) {
      if (v.archived) continue;
      map[v.key] = items.filter((r) => {
        if (v.status && r.status !== v.status) return false;
        if (v.match && !v.match(r)) return false;
        return true;
      }).length;
    }
    return map;
  }, [data]);

  function toggleMethod(m: PaymentMethod) {
    setMethodFilter((cur) => (cur === m ? null : m));
  }

  // Strip the leading attention emoji for the heading display (matches original behaviour).
  const viewHeading = t(view.nameKey).replace(/^⚠\s*/, '');

  return (
    <div className="cl row full m-scroll m-stack" style={{ minWidth: 0 }}>
      {/* Saved views rail */}
      <div className="col" style={{ width: 220, background: '#fff', borderRight: '1px solid var(--line)', flexShrink: 0 }}>
        <div className="row between center" style={{ padding: '14px 14px', borderBottom: '1px solid var(--line-2)' }}>
          <div className="h3">{t('views_heading')}</div>
          <div className="meta" style={{ cursor: 'default' }}>+</div>
        </div>
        <div className="col gap-4" style={{ padding: 6 }}>
          {VIEWS.map((v) => {
            const active = v.key === viewKey;
            return (
              <button
                key={v.key}
                type="button"
                onClick={() => setViewKey(v.key)}
                className="row between center"
                style={{
                  padding: '6px 10px',
                  borderRadius: 4,
                  border: 'none',
                  textAlign: 'left',
                  cursor: 'pointer',
                  background: active ? 'var(--navy)' : 'transparent',
                  color: active ? '#fff' : 'var(--navy-3)',
                  fontSize: 13,
                  fontFamily: 'inherit',
                }}
              >
                <span>{t(v.nameKey)}</span>
                {counts[v.key] !== undefined && (
                  <span className="mono" style={{ fontSize: 12, opacity: 0.7 }}>
                    {counts[v.key]}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <div className="divider" />
        <div className="col" style={{ padding: 12, gap: 8 }}>
          <div className="h3">{t('quick_filters_heading')}</div>
          <div className="row gap-6" style={{ flexWrap: 'wrap' }}>
            {activeOffices.map((o) => (
              <button
                key={o.code}
                type="button"
                className="pill"
                onClick={() => setSearch(o.name)}
                style={{ fontSize: 12, border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}
              >
                {o.name}
              </button>
            ))}
          </div>
          <div className="row gap-6" style={{ flexWrap: 'wrap' }}>
            {METHOD_FILTERS.map((m) => {
              const active = methodFilter === m;
              return (
                <button
                  key={m}
                  type="button"
                  className={`pill ${active ? 'blue' : ''}`}
                  onClick={() => toggleMethod(m)}
                  style={{ fontSize: 12, border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}
                >
                  {PAYMENT_LABEL[m]}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="col grow m-scroll" style={{ background: 'var(--paper)', overflow: 'hidden', minWidth: 0 }}>
        <div className="row center between" style={{ padding: '14px 22px', borderBottom: '1px solid var(--line)', background: '#fff' }}>
          <div className="col">
            <div className="row gap-8 center">
              <span className="h1">{viewHeading}</span>
              <span className="pill red">{rows.length}</span>
            </div>
            <div className="meta">{t(view.descKey)}</div>
          </div>
          <div className="row gap-8 center">
            <div className="input" style={{ width: 220 }}>
              <SearchIcon />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('search_placeholder')}
                aria-label={t('search_placeholder')}
                style={{
                  border: 'none',
                  outline: 'none',
                  background: 'transparent',
                  font: 'inherit',
                  color: 'inherit',
                  flex: 1,
                  minWidth: 0,
                }}
              />
            </div>
            {/* Bulk actions and CSV export are not built yet — no placeholder buttons. */}
          </div>
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: '14px 22px' }}>
          {loading ? (
            <LoadingState label={t('loading_orders')} />
          ) : error ? (
            <ErrorState message={error.message} onRetry={reload} />
          ) : rows.length === 0 ? (
            <EmptyState title={t('empty_title')} hint={t('empty_hint')} />
          ) : (
            <>
              <div className="panel" style={{ overflow: 'hidden' }}>
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: 24 }}>
                        <span
                          style={{ width: 14, height: 14, border: '1px solid var(--line)', borderRadius: 2, display: 'inline-block' }}
                        />
                      </th>
                      <th>{t('col_id')}</th>
                      <th>{t('col_date')}</th>
                      <th>{t('col_client')}</th>
                      <th>{t('col_agent')}</th>
                      <th>{t('col_payment')}</th>
                      <th>{t('col_status')}</th>
                      <th>{t('col_age')}</th>
                      {showMoney && <th style={{ textAlign: 'right' }}>{t('col_total')}</th>}
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const kind = attentionOf(r);
                      return (
                        <tr key={r.id} style={{ background: rowTint(kind) }}>
                          <td>
                            <span
                              style={{ width: 14, height: 14, border: '1px solid var(--line)', borderRadius: 2, display: 'inline-block' }}
                            />
                          </td>
                          <td className="mono" style={{ fontSize: 13 }}>
                            {r.code}
                          </td>
                          <td className="meta">{dateLabel(r.createdAt)}</td>
                          <td>
                            <div style={{ fontSize: 14, fontWeight: 500 }}>{r.clientName}</div>
                          </td>
                          <td>
                            <div style={{ fontSize: 13 }}>{r.agentName}</div>
                          </td>
                          <td>
                            <span style={{ fontSize: 13 }}>{PAYMENT_LABEL[r.paymentMethod]}</span>
                          </td>
                          <td>
                            <div className="col gap-4">
                              <StatusPill status={r.status} />
                              {kind === 'stuck' && (
                                <span className="meta" style={{ color: 'var(--red)', fontSize: 12 }}>
                                  {t('stuck_badge', { days: String(daysSince(r.createdAt)) })}
                                </span>
                              )}
                              {kind === 'delivery' && (
                                <span className="meta" style={{ color: 'var(--red)', fontSize: 12 }}>
                                  {t('delivery_badge')}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="meta">{ageLabel(r.createdAt)}</td>
                          {showMoney && (
                            <td className="mono" style={{ textAlign: 'right', fontWeight: 500 }}>
                              {euros(r.totalCents)}
                            </td>
                          )}
                          <td style={{ textAlign: 'right' }}>
                            <div className="row gap-6" style={{ justifyContent: 'flex-end' }}>
                              {kind && (
                                <button
                                  type="button"
                                  className="btn sm danger"
                                  onClick={() => navigate(`/admin/orders/${r.id}`)}
                                >
                                  {t('btn_resolve')}
                                </button>
                              )}
                              <button
                                type="button"
                                className="btn sm"
                                onClick={() => navigate(`/admin/orders/${r.id}`)}
                              >
                                {t('btn_open')}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="row between center" style={{ padding: '10px 4px' }}>
                <div className="meta">
                  {t('showing', { shown: String(rows.length), total: String(data?.total ?? rows.length) })}
                </div>
                <div className="meta">‹ 1 ›</div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
